import React, { useEffect, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslations } from 'hooks/useTranslations';
import { StyledPageTitle } from 'theme/styles';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from 'components/atoms/Loader';
import { ILicenceAuthorityPrintoutSettingsFormValues } from 'models/licenceAuthority';
import { useLicenceAuthorities } from '../hooks';
import LicenceAuthoritiesPrintoutSettingsForm from '../LicenceAuthoritiesPrintoutSettingsForm';

const LicenceAuthoritiesPrintoutSettingsEdit = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initialFormValues, setInitialFormValues] =
    useState<ILicenceAuthorityPrintoutSettingsFormValues>();
  const { printoutSettings, getPrintoutSettings, editPrintoutSettings } =
    useLicenceAuthorities();

  useEffect(() => {
    (async function init() {
      setIsLoading(true);
      getPrintoutSettings(id);
      setIsLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    if (!isLoading && printoutSettings) {
      setInitialFormValues({
        username: printoutSettings.username,
        host: printoutSettings.host,
        port: printoutSettings.port,
        hostFingerprints: printoutSettings.hostFingerprints,
        pgpPublicKey: printoutSettings.pgpForeignPublicKey,
        outboundPath: printoutSettings.outboundPath,
        inboundPath: printoutSettings.inboundPath,
        sendHours: printoutSettings.sendHours,
      });
    }
  }, [isLoading, printoutSettings]);

  const onSubmit = async (data: ILicenceAuthorityPrintoutSettingsFormValues) => {
    setIsSubmitLoading(true);
    const res = await editPrintoutSettings(id, printoutSettings?.version ?? 0, data);
    if (res) {
      navigate(ERouteLinks.DetailsLicenceAuthoritiesPrintoutSettings.replace(':id', id));
    }
    setIsSubmitLoading(false);
  };

  const onCancel = () =>
    navigate(ERouteLinks.DetailsLicenceAuthoritiesPrintoutSettings.replace(':id', id));

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.administration.name'), route: ERouteLinks.Administration },
          { label: t('licenceAuthorities.header'), route: ERouteLinks.LicenceAuthorities },
          {
            label: t('licenceAuthorities.detailsLicenceAuthority.header'),
            route: ERouteLinks.DetailsLicenceAuthorities.replace(':id', id),
          },
          {
            label: t('licenceAuthorities.printoutSettingsDetails.header'),
            route: ERouteLinks.DetailsLicenceAuthoritiesPrintoutSettings.replace(':id', id),
          },
          t('licenceAuthorities.editPrintoutSettings.header'),
        ]}
      />
      <StyledPageTitle variant="h4">
        {t('licenceAuthorities.editPrintoutSettings.header')}
      </StyledPageTitle>

      {isLoading && <Loader isLoading={true} />}
      {!isLoading && initialFormValues && (
        <LicenceAuthoritiesPrintoutSettingsForm
          onSubmit={onSubmit}
          isSubmitLoading={isSubmitLoading}
          initialFormValues={initialFormValues}
          onCancel={onCancel}
        />
      )}
    </>
  );
};

export default LicenceAuthoritiesPrintoutSettingsEdit;
